import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, ShieldCheck } from "lucide-react";

const LOGO = "/smartfx-logo.png";

export default function VerifyEmail() {
  const { playTransition, endTransition, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const inputs = useRef([]);

  useEffect(() => {
    if (!email) navigate("/register");
  }, [email, navigate]);

  const code = digits.join("");

  const setDigit = (i, v) => {
    const clean = v.replace(/\D/g, "").slice(-1);
    setDigits((d) => {
      const next = [...d];
      next[i] = clean;
      return next;
    });
    if (clean && i < 5) inputs.current[i + 1]?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputs.current[i - 1]?.focus();
  };

  const onPaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (text.length) {
      e.preventDefault();
      const next = text.split("");
      setDigits(["", "", "", "", "", ""].map((_, i) => next[i] || ""));
      inputs.current[Math.min(text.length, 5)]?.focus();
    }
  };

  const verify = async () => {
    setError("");
    if (code.length !== 6) { setError("Enter the 6-digit code we sent to your email."); return; }
    setBusy(true);
    try {
      await api.post("/auth/verify-code", { email, code });
      await refreshUser();
      toast.success("Email verified. Welcome to Smart FX!");
      await playTransition("login");
      navigate("/app");
      setTimeout(endTransition, 700);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const resend = async () => {
    setError("");
    setResending(true);
    try {
      await api.post("/auth/resend-code", { email });
      toast.success("A new code is on its way to your email.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] grain flex flex-col">
      <div className="p-6">
        <Link to="/register" data-testid="back-register-link" className="inline-flex items-center gap-2 text-sm text-[#888] hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-20">
        <div className="w-full max-w-md fade-up">
          <div className="flex items-center gap-2 mb-8">
            <img src={LOGO} alt="Smart FX" className="h-11 w-auto object-contain" />
            <span className="font-heading text-xl font-bold tracking-tight">SMART FX</span>
          </div>
          <div className="h-12 w-12 rounded-full bg-[#1C8CFF]/15 flex items-center justify-center mb-5">
            <ShieldCheck className="h-6 w-6 text-[#1C8CFF]" />
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-1">Verify your email</h1>
          <p className="text-[#888] text-sm mb-8">
            We sent a 6-digit code to <span className="text-white">{email}</span>. Enter it below to activate your account.
          </p>

          <div className="flex gap-2.5 justify-between" data-testid="code-inputs" onPaste={onPaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputs.current[i] = el)}
                data-testid={`code-digit-${i}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                className="w-12 h-14 text-center text-2xl font-bold bg-[#0F0F0F] border border-[#222] rounded-sm text-white outline-none focus:border-white transition-colors"
              />
            ))}
          </div>

          {error && <p data-testid="verify-error" className="text-sm text-[#FF3B30] mt-4">{error}</p>}

          <Button onClick={verify} data-testid="verify-code-button" disabled={busy}
            className="w-full h-11 rounded-sm bg-white text-black hover:bg-[#e5e5e5] font-semibold transition-colors mt-6">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Continue"}
          </Button>

          <p className="text-sm text-[#888] mt-6">
            Didn't get the code?{" "}
            <button type="button" onClick={resend} disabled={resending} data-testid="resend-code-button"
              className="text-white underline underline-offset-4 disabled:opacity-60">
              {resending ? "Sending..." : "Resend code"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
